import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const itemCategories = {
  // Auditiv Items
  'ba_aud_01': 'Auditiv',
  'ba_aud_0111': 'Auditiv',


  // Visuell Items
  'ba_vis_01': 'Visuell',
  'ba_vis_0111': 'Visuell',


   // Taktil Items
  'ba_tak_01': 'Taktil',
  'ba_tak_0111': 'Taktil',
  
  
  // Geruch/Geschmack Items,
  'ba_ge_01': 'Geruch_Geschmack',
  'ba_ge_0111': 'Geruch_Geschmack',
  
  
  // Unpredictability Items
  'ba_unp_01': 'Unvorhersehbarkeit',
  'ba_unp_0111': 'Unvorhersehbarkeit',

  
  // Sonstiges Items
  'ba_so_01': 'Sonstiges',
  'ba_so_0111': 'Sonstiges',
  
};

const getGermanWeekday = (dateStr) => {
  const weekdays = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
  
  // Parsing date here
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  
  // Check if the date is valid
  if (isNaN(date.getTime())) {
    return {
      abbreviation: '??',
      isWeekend: false
    };
  }
  
  return {
    abbreviation: weekdays[date.getDay()],
    isWeekend: date.getDay() === 0 || date.getDay() === 6
  };
};

const calculateDayNumber = (responses) => {
  const dateMap = {};
  const allDates = [];
  
  // Collect all dates from responses
  responses.forEach(response => {
    const parts = response.session_id.split('_');
    if (parts.length >= 2) {
      const dateStr = parts[1];
      // Validating date format
      if (dateStr && dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
        allDates.push(dateStr); 
      }
    }
  });

  if (allDates.length === 0) return { dayMap: {}, dateMap: {}, allWeekdays: [] };

  const sortedDates = [...new Set(allDates)].sort();
  
  // Parsing dates
  const parsedDates = sortedDates.map(dateStr => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day); // month is 0-indexed in Date
  }).filter(date => !isNaN(date.getTime())); // Filter out invalid dates

  if (parsedDates.length === 0) return { dayMap: {}, dateMap: {}, allWeekdays: [] };

  const startDate = new Date(Math.min(...parsedDates.map(d => d.getTime())));
  const endDate = new Date(Math.max(...parsedDates.map(d => d.getTime())));
  
  // Generate all weekdays in range
  const allWeekdays = [];
  let currentDate = new Date(startDate);
  let dayCounter = 0;
  

  while (currentDate <= endDate) {
    //const dateStr = currentDate.toISOString().split('T')[0];
    const dateStr = currentDate.getFullYear() + '-' +
      String(currentDate.getMonth() + 1).padStart(2, '0') + '-' +
      String(currentDate.getDate()).padStart(2, '0');
    const { abbreviation, isWeekend } = getGermanWeekday(dateStr);
    
    if (!isWeekend) {
      dayCounter++;
      dateMap[dateStr] = {
        dayNumber: dayCounter,
        label: `${dayCounter} (${abbreviation})`,
        date: dateStr
      };
      allWeekdays.push(dateStr);
    }
    currentDate.setDate(currentDate.getDate() + 1);
  }

  // Create session to day mapping
  const dayMap = {};
  responses.forEach(response => {
    const parts = response.session_id.split('_');
    if (parts.length >= 2) {
      const dateStr = parts[1];
      if (dateMap[dateStr]) {
        dayMap[response.session_id] = dateMap[dateStr];
      }
    }
  });

  return {
    dayMap,
    dateMap,
    allWeekdays
  };
};

const processMoodData = (responses, items, moodType) => {
  const { dayMap, dateMap, allWeekdays } = calculateDayNumber(responses);
  const dayData = {};

  // Initialize days - add hasBarrier flag
  allWeekdays.forEach(dateStr => {
    const dayInfo = dateMap[dateStr];
    dayData[`Day_${dayInfo.dayNumber}`] = {
      name: dayInfo.label,
      date: dayInfo.date,
      [moodType]: null,
      count: 0,
      sum: 0,
      hasBarrier: false
    };
  });

  // First pass: Process barrier items
  responses.forEach(response => {
    // Skip if not a barrier item or no session mapping
    if (!Object.keys(itemCategories).includes(response.item_name) || !dayMap[response.session_id]) return;
    
    let antwortParts = response.item_resp.split(';');
    let antwortValue = antwortParts[1] || antwortParts[0]; // use 2nd part if exists

    const cleanValue = antwortValue?.trim().replace(/[^0-9.]/g, '');
    if (cleanValue === '1' || parseFloat(cleanValue) === 1) {
      const dayNumber = dayMap[response.session_id].dayNumber;
      const dayKey = `Day_${dayNumber}`;
      dayData[dayKey].hasBarrier = true;
    }

  });

  // Second pass: Process mood items
  responses.forEach(response => {
    // Skip if not a mood item or no session mapping
    if (!items.includes(response.item_name) || !dayMap[response.session_id]) return;
    
    // Skip barrier items
    if (Object.keys(itemCategories).includes(response.item_name)) return;

    // Normal mood item processing
    const value = parseFloat(response.item_resp);
    if (isNaN(value)) return;

    const dayNumber = dayMap[response.session_id].dayNumber;
    const dayKey = `Day_${dayNumber}`;

    if (!dayData[dayKey].time) {
      const timeStr = response.session_id.split('_')[2];  // "10-32-03"
      const [hour, minute] = timeStr.split('-');
      dayData[dayKey].time = `${hour}:${minute}`;
    }
    
    dayData[dayKey].sum += value;
    dayData[dayKey].count++;
  });

  // Calculate averages
  Object.values(dayData).forEach(day => {
    if (day.count > 0) {
      const exactValue = day.sum / day.count;
      day[`${moodType}Exact`] = exactValue;
      day[moodType] = Math.round(exactValue);
    }
  });

  return Object.values(dayData).sort((a, b) => 
    parseInt(a.name.split(' ')[0]) - parseInt(b.name.split(' ')[0])
  );
};

const lineChartCategoryColors = {
  'Keine Barrieren': '#6ba3d3',
  'Barrieren': '#F29400'
};


export const PositiveMoodChart = ({ responses }) => {
  const positiveItems = ['aff_1_pa', 'aff_7_pa', 'aff_12_pa'];
  const data = processMoodData(responses, positiveItems, 'Positiv');

  return (
    <div>
    <h4>Stimmung</h4>
      <p>
      Das Diagramm zeigt, wie das Kind seine Stimmung an den einzelnen Schultagen eingeschätzt hat. 
      Für verschiedene Aussagen zur Stimmung (z. B. „Ich bin gut gelaunt“) konnte das Kind auf einer 
      5-stufigen Skala von 0 bis 4 angeben, wie sehr die jeweilige Aussage zutrifft. Dabei reicht die 
      Skala von „stimmt genau“ (4) bis „stimmt gar nicht“ (0). Auf der X-Achse ist der Zeitraum der 
      Befragung dargestellt. Die Y-Achse zeigt die jeweilige Einschätzung auf der Skala von 0 bis 4. 
      Die Ergebnisse werden in zwei Diagrammen dargestellt: positive Stimmung und negative Stimmung. 
      Die blauen Punkte zeigen Tage, an denen keine Barrieren angegeben wurden. Die orangenen Punkte 
      kennzeichnen Tage, an denen zusätzlich Barrieren wahrgenommen wurden. So können mögliche Zusammenhänge 
      zwischen der Stimmung und dem Auftreten von Barrieren betrachtet werden. 
      </p>
    <div style={{ width: '100%', height: 500, marginBottom: '2rem' }}>
      <h4>Positive Stimmung im Verlauf</h4>
      <ResponsiveContainer>
        <LineChart 
          data={data}
          margin={{ top: 20, right: 60, bottom: 30, left: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAxis 
            dataKey="name" 
            angle={-45} 
            textAnchor="end" 
            height={50}
            tick={{ fill: '#000000' }}
            padding={{ left: 20, right: 20 }} 
          />
          <YAxis 
            domain={[0, 4]} 
            tickCount={5} 
            allowDecimals={false}
            padding={{ top: 20, bottom: 20 }}
            label={{ 
              value: 'Positive Stimmung', 
              angle: -90, 
              position: 'insideLeft',
              fill: '#000000',
              style: { 
                textAnchor: 'middle',
                dominantBaseline: 'middle',
                fontSize: '14px'
              },
              offset: 10 
            }}
          />
          <Tooltip 
            formatter={(value, name, props) => {
              const dataKey = props.dataKey;
              const exact = props.payload?.[`${dataKey}Exact`];
              const barrierText = props.payload?.hasBarrier ? ' (Barrieren)' : ' (Keine Barrieren)';
              return [
                exact !== undefined ? exact.toFixed(2) : value.toFixed(2),
                name + barrierText
              ];
            }}
            labelFormatter={(label, payload) => {
              const p = payload[0]?.payload;
              if (!p) return label;

              // Add Date + Time
              return `${label} • ${p.date} • ${p.time ?? '??:??'}`;
            }}
          />
          <Legend 
            layout="vertical" 
            verticalAlign="middle" 
            align="right"
            wrapperStyle={{ 
                right: 50, 
                padding: '0 10px'
            }}
            payload={Object.entries(lineChartCategoryColors).map(([value, color]) => ({
              value,
              type: 'line',
              color,
              id: value
            }))}
          />
          <Line 
            type="monotone" 
            dataKey="Positiv" 
            stroke="#AEAEAE"
            strokeWidth={2}
            dot={(props) => {
              const { cx, cy, payload, value } = props;
              if (value === null || value === undefined) return null;
              
              return (
                <circle
                  cx={cx}
                  cy={cy}
                  r={6}
                  fill={payload.hasBarrier ? '#F29400' : '#6ba3d3'}
                  stroke={payload.hasBarrier ? '#F29400' : '#6ba3d3'}
                  strokeWidth={2}
                />
              );
            }}
            activeDot={{
              r: 8,
              fill: (props) => props.payload.hasBarrier ? '#F29400' : '#6ba3d3',
              stroke: (props) => props.payload.hasBarrier ? '#F29400' : '#6ba3d3',
              strokeWidth: 2
            }}
            connectNulls={true}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
    </div>
  );
};

export const NegativeMoodChart = ({ responses }) => {
  const negativeItems = ['aff_2_na', 'aff_8_na', 'aff_11_na'];
  const data = processMoodData(responses, negativeItems, 'Negativ');

  return (
    <div style={{ width: '100%', height: 500, marginBottom: '2rem' }}>
      <h4>Negative Stimmung im Verlauf</h4>
      <ResponsiveContainer>
        <LineChart 
          data={data}
          margin={{ top: 20, right: 60, bottom: 30, left: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAxis 
            dataKey="name" 
            angle={-45} 
            textAnchor="end" 
            height={50}
            tick={{ fill: '#000000' }}
            padding={{ left: 20, right: 20 }}
          />
          <YAxis 
            domain={[0, 4]} 
            tickCount={5} 
            allowDecimals={false}
            padding={{ top: 20, bottom: 20 }}
            label={{ 
              value: 'Negative Stimmung', 
              angle: -90, 
              position: 'insideLeft',
              fill: '#000000',
              style: { 
                textAnchor: 'middle',
                dominantBaseline: 'middle',
                fontSize: '14px'
              },
              offset: 10
            }}
          />
          <Tooltip 
            formatter={(value, name, props) => {
              const dataKey = props.dataKey;
              const exact = props.payload?.[`${dataKey}Exact`];
              const barrierText = props.payload?.hasBarrier ? ' (Barrieren)' : ' (Keine Barrieren)';
              return [
                exact !== undefined ? exact.toFixed(2) : value.toFixed(2),
                name + barrierText
              ];
            }}
            labelFormatter={(label) => label}
          />
          <Legend 
            layout="vertical" 
            verticalAlign="middle" 
            align="right"
            wrapperStyle={{ 
                right: 50, 
                padding: '0 10px'
            }}
            payload={Object.entries(lineChartCategoryColors).map(([value, color]) => ({
              value,
              type: 'line',
              color,
              id: value
            }))}
          />
          <Line 
            type="monotone" 
            dataKey="Negativ"
            stroke="#AEAEAE"
            strokeWidth={2}
            dot={(props) => {
              const { cx, cy, payload, value } = props;
              if (value === null || value === undefined) return null;
              
              return (
                <circle
                  cx={cx}
                  cy={cy}
                  r={6}
                  fill={payload.hasBarrier ? '#F29400' : '#6ba3d3'}
                  stroke={payload.hasBarrier ? '#F29400' : '#6ba3d3'}
                  strokeWidth={2}
                />
              );
            }}
            activeDot={{
              r: 8,
              fill: (props) => props.payload.hasBarrier ? '#F29400' : '#6ba3d3',
              stroke: (props) => props.payload.hasBarrier ? '#F29400' : '#6ba3d3',
              strokeWidth: 2
            }}
            connectNulls={true}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};